/*
 * *****************************************************************************
 *  Copyright (C)  Motorola Solutions, INC.
 *  All Rights Reserved.
 *  Motorola Solutions Confidential Restricted.
 *  ******************************************************************************
 */

import { Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { catchError, filter, map, mergeMap, switchMap } from 'rxjs/operators';
import {
    fetchDtmfMessages,
    fetchDtmfMessagesFail,
    fetchDtmfMessagesSuccess,
    fetchPredefinedSmsMessages,
    fetchPredefinedSmsMessagesFail,
    fetchPredefinedSmsMessagesSuccess,
    fetchPredefinedTddMessages,
    fetchPredefinedTddMessagesFail,
    fetchPredefinedTddMessagesSuccess,
    fetchRealtimeMessages,
    fetchReleasedCallMessages,
    fetchReleasedCallMessagesFail,
    fetchReleasedCallMessagesSuccess,
    fetchRttMessages,
    fetchRttMessagesFail,
    fetchRttMessagesSuccess,
    fetchSmsMessages,
    fetchSmsMessagesFail,
    fetchSmsMessagesSuccess,
    fetchTddMessages,
    fetchTddMessagesFail,
    fetchTddMessagesSuccess,
    fetchTextCallHistory,
    fetchTextCallHistoryFail,
    fetchTextCallHistorySuccess,
    initializeDtmfMessageState,
    initializeRttMessageState,
    initializeSmsMessageState,
    initializeTddMessageState,
    messageEffectsInitialized,
    purgeMessages,
    tddChallenge
} from './message.actions';
import {
    answerSuccess,
    deleteManyCalls,
    dialSuccess,
    joinCallSuccess,
    openCall,
    purgeHistoricalCall,
    redialSuccess,
    sendTddMessage,
    unholdSuccess
} from '../../call/+state/call.actions';
import { defer, Observable, of, retry, timer } from 'rxjs';
import { acdAnswerRequestSuccess } from '../../user/+state/user.actions';
import { TddMessageService } from '../services/tdd-message.service';
import { SmsMessageService } from '../services/sms-message.service';
import { SystemErrorMessageService } from '../services/system-error-message.service';
import { DtfmMessageService } from '../services/dtmf-message.service';
import { AnalyticsService } from '../../call/services/analytics.service';
import { RttMessageService } from '../services/rtt-message.service';
import { HistoricalTextMessage } from '../model/historical-text-message';
import { SkipperService } from '../../core/services/skipper.service';
import { concatLatestFrom } from '@ngrx/operators';
import { selectCall, selectOpenCalls } from '../../call/+state/call.selectors';
import { CtcAdapterService } from '../../core/services/ctc-adapter.service';
import { SystemLogMessageService } from '../services/system-log-message.service';

@Injectable()
export class MessageEffects implements OnInitEffects {
    constructor(
        private smsMessageService: SmsMessageService,
        private skipperService: SkipperService,
        private tddMessageService: TddMessageService,
        private rttMessageService: RttMessageService,
        private dtmfMessageService: DtfmMessageService,
        private ctcAdapterService: CtcAdapterService,
        private systemLogMessageService: SystemLogMessageService,
        private systemErrorMessageService: SystemErrorMessageService,
        private callHistoryService: AnalyticsService,
        private actions$: Actions,
        private store: Store
    ) {}

    ngrxOnInitEffects(): Action {
        return messageEffectsInitialized();
    }

    initializeDtmfMessages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initializeDtmfMessageState),
            concatLatestFrom(() => this.store.select(selectOpenCalls)),
            mergeMap(([{ clusterName }, openedCalls]) =>
                openedCalls
                    .filter((call) => !clusterName || call.clusterName === clusterName)
                    .map((call) => fetchDtmfMessages({ callId: call.uuid, clusterName: call.clusterName }))
            )
        )
    );

    initializeRttMessages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initializeRttMessageState),
            concatLatestFrom(() => this.store.select(selectOpenCalls)),
            mergeMap(([{ clusterName }, openedCalls]) =>
                openedCalls
                    .filter((call) => !clusterName || call.clusterName === clusterName)
                    .map((call) => fetchRttMessages({ callId: call.uuid, clusterName: call.clusterName }))
            )
        )
    );

    initializeTddMessages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initializeTddMessageState),
            concatLatestFrom(() => this.store.select(selectOpenCalls)),
            mergeMap(([{ clusterName }, openedCalls]) =>
                openedCalls
                    .filter((call) => !clusterName || call.clusterName === clusterName)
                    .map((call) => fetchTddMessages({ callId: call.uuid, clusterName: call.clusterName }))
            )
        )
    );

    initializeSmsMessages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initializeSmsMessageState),
            concatLatestFrom(() => this.store.select(selectOpenCalls)),
            mergeMap(([{ clusterName }, openedCalls]) =>
                openedCalls
                    .filter((call) => !clusterName || call.clusterName === clusterName)
                    .map((call) => fetchSmsMessages({ callId: call.uuid, clusterName: call.clusterName }))
            )
        )
    );

    fetchOpenedCallMessages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(openCall),
            concatLatestFrom(({ callId }) => this.store.select(selectCall(callId))),
            map(([, call]) =>
                call.status === 'RELEASED'
                    ? fetchReleasedCallMessages({ callId: call.uuid })
                    : fetchRealtimeMessages({ callId: call.uuid, clusterName: call.clusterName })
            )
        )
    );

    initializeRealtimeMessages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(unholdSuccess, redialSuccess, joinCallSuccess, acdAnswerRequestSuccess, answerSuccess, dialSuccess, fetchRealtimeMessages),
            switchMap(({ callId, clusterName }) => [
                fetchTddMessages({ callId, clusterName }),
                fetchSmsMessages({ callId, clusterName }),
                fetchRttMessages({ callId, clusterName }),
                fetchDtmfMessages({ callId, clusterName })
            ])
        )
    );

    tddChallenge$ = createEffect(() =>
        this.actions$.pipe(
            ofType(tddChallenge),
            map(({ clusterName, callId, language, predefined, message }) =>
                sendTddMessage({
                    clusterName,
                    callId,
                    language,
                    predefined,
                    message
                })
            )
        )
    );

    fetchSmsMessages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchSmsMessages),
            switchMap((action) =>
                this.smsMessageService.requestSmsMessages(action.callId, action.clusterName).pipe(
                    map((response) => fetchSmsMessagesSuccess({ messages: response })),
                    catchError((err: Error) => of(fetchSmsMessagesFail({ payload: err.message })))
                )
            )
        )
    );

    fetchRttMessages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchRttMessages),
            switchMap((action) =>
                this.rttMessageService.requestRttMessages(action.callId, action.clusterName).pipe(
                    map((response) => fetchRttMessagesSuccess({ messages: response })),
                    catchError((err: Error) => of(fetchRttMessagesFail({ payload: err.message })))
                )
            )
        )
    );

    fetchSmsHistoricalMessages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchTextCallHistory),
            switchMap((action) =>
                this.callHistoryService.requestTextCallHistory(action.callingNumber, action.callId, action.nenaCallId).pipe(
                    map((messages) => messages.filter((analyticsMessage) => analyticsMessage.ctcCallId !== action.callId)),
                    map((messages) =>
                        fetchTextCallHistorySuccess({
                            referenceKey: action.callingNumber,
                            messages: messages.map((analyticsMessage) => HistoricalTextMessage.toMessage(analyticsMessage))
                        })
                    ),
                    catchError((err: any) => {
                        console.error(`fetchSmsHistoricalMessages$ ERROR — status=${err?.status} url=${err?.url} message="${err?.message}"`, err);
                        return of(fetchTextCallHistoryFail({ payload: err.message }));
                    })
                )
            )
        )
    );

    fetchDtmfMessages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchDtmfMessages),
            switchMap((action) =>
                this.dtmfMessageService.requestDtmfMessages(action.callId, action.clusterName).pipe(
                    map((response) => fetchDtmfMessagesSuccess({ messages: response })),
                    catchError((err: Error) => of(fetchDtmfMessagesFail({ payload: err.message })))
                )
            )
        )
    );

    fetchTddMessages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchTddMessages),
            switchMap((action) =>
                this.tddMessageService.requestTddMessages(action.callId, action.clusterName).pipe(
                    map((response) => fetchTddMessagesSuccess({ messages: response })),
                    catchError((err: Error) => of(fetchTddMessagesFail({ payload: err.message })))
                )
            )
        )
    );

    fetchReleasedCallMessages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchReleasedCallMessages),
            switchMap((action) =>
                this.ctcAdapterService.requestMessages(action.callId).pipe(
                    map((response) => fetchReleasedCallMessagesSuccess({ messages: response })),
                    catchError((err: Error) => of(fetchReleasedCallMessagesFail({ payload: err.message })))
                )
            )
        )
    );

    fetchReleasedCallMessagesSuccess$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchReleasedCallMessagesSuccess),
            mergeMap(({ messages }) => [
                fetchDtmfMessagesSuccess({ messages: messages.filter(({ type }) => type === 'DTMF') }),
                fetchSmsMessagesSuccess({ messages: messages.filter(({ type }) => type === 'SMS') }),
                fetchTddMessagesSuccess({ messages: messages.filter(({ type }) => type === 'TDD') }),
                fetchRttMessagesSuccess({ messages: messages.filter(({ type }) => type === 'RTT') })
            ])
        )
    );

    fetchPredefinedTddMessages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchPredefinedTddMessages),
            switchMap(() => this.skipperService.initialized$),
            filter((initialized) => !!initialized),
            switchMap(() =>
                defer(() => this.skipperService.requestPredefinedTdd()).pipe(
                    map((response) => fetchPredefinedTddMessagesSuccess({ messages: response })),
                    retry({ delay: () => timer(5000) }),
                    catchError((err: Error) => of(fetchPredefinedTddMessagesFail({ payload: err.message })))
                )
            )
        )
    );

    fetchPredefinedSmsMessages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchPredefinedSmsMessages),
            switchMap(() => this.skipperService.initialized$),
            filter((initialized) => !!initialized),
            switchMap(() =>
                defer(() => this.skipperService.requestPredefinedSms()).pipe(
                    map((response) => fetchPredefinedSmsMessagesSuccess({ messages: response })),
                    retry({ delay: () => timer(5000) }),
                    catchError((err: Error) => of(fetchPredefinedSmsMessagesFail({ payload: err.message })))
                )
            )
        )
    );

    purgeHistoricalMessages$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(purgeHistoricalCall),
            map(({ uuid }) => purgeMessages({ uuids: [uuid] }))
        )
    );

    purgeOrphanedHistoricalMessages$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(deleteManyCalls),
            map(({ callIds }) => purgeMessages({ uuids: callIds }))
        )
    );
}
